import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, LogIn, UserPlus } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") navigate("/");
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate("/");
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        toast.success("Check your email to confirm your account");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Signed in successfully");
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center px-4"
      data-testid="auth-page"
      data-route="/auth"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex h-12 w-12 mx-auto items-center justify-center rounded-md bg-accent text-accent-foreground font-bold text-lg font-mono mb-4">
            DI
          </div>
          <h1 className="text-xl font-semibold text-foreground">Sovereign Executive Hub</h1>
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mt-1">
            Secure Access Required
          </p>
        </div>

        <div className="panel-elevated p-6 rounded-lg">
          <div className="flex gap-2 mb-6">
            <Button
              variant={mode === "login" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs font-mono"
              onClick={() => setMode("login")}
            >
              <LogIn className="h-3 w-3 mr-1.5" /> Sign In
            </Button>
            <Button
              variant={mode === "signup" ? "default" : "outline"}
              size="sm"
              className="flex-1 text-xs font-mono"
              onClick={() => setMode("signup")}
            >
              <UserPlus className="h-3 w-3 mr-1.5" /> Register
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Full Name</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="David Isei" required className="mt-1" />
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="operator@hub.mil" required className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} className="mt-1" />
            </div>
            <Button type="submit" className="w-full font-mono text-xs" disabled={loading}>
              {loading ? "Processing…" : mode === "login" ? "Authenticate" : "Request Access"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate("/forgot-password")}
              className="text-[10px] text-muted-foreground hover:text-accent font-mono uppercase tracking-wider"
            >
              Forgot Password?
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-muted-foreground">
          <Shield className="h-3 w-3 text-accent" />
          <span className="text-[10px] uppercase tracking-wider font-mono">AES-256 Encrypted · Zero-Trust Architecture</span>
        </div>
      </div>
    </div>
  );
}
